import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

const ClientsEdit = () => {
  const navigate = useNavigate();
  const params = useParams();
  const clientId = params.data || params.id; // supports /clientsEdit/:id or :data

  // form fields
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [redirect_url, setRedirectUrl] = useState("");
  const [email, setEmail] = useState("");

  // record
  const [rowData, setRowData] = useState({ id: "", name: "" });

  // validation + ui state
  const [nameErr, setNameErr] = useState("");
  const [domainErr, setDomainErr] = useState("");
  const [redirect_urlErr, setRedirectUrlErr] = useState("");
  const [emailValidationErrors, setEmailValidationErrors] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(true);

  // load one client
  useEffect(() => {
    if (!clientId) return;
    fetch(`${process.env.REACT_APP_API_URL}/clients/${clientId}`, {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        authorization:
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImFkbWluQGF1dGhmYXN0LmNvbSIsImlkIjo1LCJ1c2VyVHlwZSI6InN1cGVyYWRtaW4iLCJpYXQiOjE2NjM2NTE2NzV9.raQf8JTVyiM51zg6OgH1RLkQqK_QyGPO2rJa9-oYDko",
      }),
    })
      .then((r) => r.json())
      .then((res) => {
        const data = res?.data || {};
        setRowData(data);
        setName(data.name || "");
        setDomain(data.domain || "");
        setRedirectUrl(data.redirect_url || "");
        setEmail(data.email || "");
      })
      .finally(() => setLoadingRecord(false));
  }, [clientId]);

  // helpers
  const resetErrors = () => {
    setErrorMessage("");
    setNameErr("");
    setDomainErr("");
    setRedirectUrlErr("");
    setEmailValidationErrors("");
  };

  // update
  const handleUpdate = async (e) => {
    e.preventDefault();
    resetErrors();

    try {
      setShowLoading(true);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/clients/update/${rowData.id}`,
        {
          method: "PATCH",
          headers: new Headers({
            "Content-Type": "application/json",
            authorization:
              "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImFkbWluQGF1dGhmYXN0LmNvbSIsImlkIjo1LCJ1c2VyVHlwZSI6InN1cGVyYWRtaW4iLCJpYXQiOjE2NjM2NTE2NzV9.raQf8JTVyiM51zg6OgH1RLkQqK_QyGPO2rJa9-oYDko",
          }),
          body: JSON.stringify({ name, domain, redirect_url, email }),
        }
      );

      const responseData = await res.json();
      setShowLoading(false);

      if (res.status === 200 || res.status === 201) {
        navigate("/clients");
        return;
      }

      if (res.status === 400) {
        setErrorMessage(responseData?.message || "Something went wrong.");
        return;
      }

      if (res.status === 422 && Array.isArray(responseData?.errors)) {
        const getFirst = (prop) => {
          const err = responseData.errors.find((e) => e.property === prop);
          if (!err) return "";
          const vals = Object.values(err.constraints || {});
          return vals?.[0] || "";
        };
        setNameErr(getFirst("name"));
        setDomainErr(getFirst("domain"));
        setRedirectUrlErr(getFirst("redirect_url"));
        setEmailValidationErrors(getFirst("email"));
        return;
      }

      setErrorMessage(responseData?.message || "Failed to update client.");
    } catch (err) {
      console.error(err);
      setShowLoading(false);
      setErrorMessage("Network error. Please try again.");
    }
  };

  return (
    <div className="max-h-full bg-gray-50">
      {/* Top bar: left back + centered title */}
      <div className="w-full border-b flex  items-center px-4 py-3  shadow-sm">
        
          <button
            onClick={() => navigate(-1)}
            className="gap-2 rounded-md px-3 py-1.5 text-sm font-medium"
          >
            ← Back
          </button>
          <h1 className="text-lg md:text-xl font-semibold  uppercase">
            Update {loadingRecord ? "..." : rowData?.name || "Client"} Details
          </h1>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex justify-center">
          <div className="w-full max-w-lg">
            <div className="rounded-2xl bg-white shadow-md ring-1 ring-black/5">
              <div className="px-6 py-5 border-b">
                <h2 className="text-xl font-semibold text-gray-900 uppercase">
                  {loadingRecord ? "Loading…" : rowData?.name || "Client"}
                </h2>
                <p className="text-sm text-gray-500">
                  Client ID: {rowData?.id ?? "-"}
                </p>
              </div>

              <form onSubmit={handleUpdate} className="px-6 py-6">
                {/* Global error */}
                {errorMessage ? (
                  <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {errorMessage}
                  </div>
                ) : null}

                {/* Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter name"
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                      nameErr
                        ? "border-red-300 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-500"
                    }`}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  {nameErr ? (
                    <p className="mt-1 text-xs text-red-600">{nameErr}</p>
                  ) : null}
                </div>

                {/* Domain */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Domain
                  </label>
                  <input
                    type="text"
                    placeholder="https://example.com"
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                      domainErr
                        ? "border-red-300 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-500"
                    }`}
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                  />
                  {domainErr ? (
                    <p className="mt-1 text-xs text-red-600">{domainErr}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">
                      Root/app domain where SSO is allowed.
                    </p>
                  )}
                </div>

                {/* Redirect URL */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Redirect URL
                  </label>
                  <input
                    type="text"
                    placeholder="https://example.com/sso-signin"
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                      redirect_urlErr
                        ? "border-red-300 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-500"
                    }`}
                    value={redirect_url}
                    onChange={(e) => setRedirectUrl(e.target.value)}
                  />
                  {redirect_urlErr ? (
                    <p className="mt-1 text-xs text-red-600">
                      {redirect_urlErr}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">
                      Where the user should land after successful SSO.
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="text"
                    placeholder="admin@example.com"
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                      emailValidationErrors
                        ? "border-red-300 focus:ring-red-400"
                        : "border-gray-300 focus:ring-blue-500"
                    }`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {emailValidationErrors ? (
                    <p className="mt-1 text-xs text-red-600">
                      {emailValidationErrors}
                    </p>
                  ) : null}
                </div>

                {/* Actions */}
                <div className="mt-2 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    disabled={showLoading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={showLoading}
                    className="inline-flex items-center rounded-md border border-blue-300 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-700"
                  >
                    {showLoading ? (
                      <span className="inline-flex items-center gap-2">
                        <svg
                          className="h-4 w-4 animate-spin"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                        Updating…
                      </span>
                    ) : (
                      "Update Client"
                    )}
                  </button>
                </div>
              </form>
            </div>

            {!loadingRecord && !rowData?.id && (
              <p className="mt-4 text-sm text-red-600 text-center">
                Could not load client details. Please check the URL or try
                again.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientsEdit;
